import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getGroupRecipes, getGroupRecipeById } from '../../services/adminRecipeViewService';
import { CATEGORIES } from '../../services/recipeService';

const AdminGroupRecipes = () => {
  const { groupId } = useParams();

  const [groupName, setGroupName] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchRecipes = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getGroupRecipes(groupId, { search });
      setGroupName(data.groupName);
      setRecipes(data.recipes);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(fetchRecipes, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openRecipe = async (recipeId) => {
    setDetailLoading(true);
    try {
      const data = await getGroupRecipeById(groupId, recipeId);
      setSelectedRecipe(data.recipe);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load recipe');
    } finally {
      setDetailLoading(false);
    }
  };

  const categoryLabel = (value) => CATEGORIES.find((c) => c.value === value)?.label || value;

  if (loading && recipes.length === 0) return <p style={styles.status}>Loading recipes...</p>;

  return (
    <div style={styles.container}>
      <Link to="/admin" style={styles.backLink}>← Back to Admin</Link>

      {selectedRecipe ? (
        <div style={styles.card}>
          <button onClick={() => setSelectedRecipe(null)} style={styles.backBtn}>← Back to {groupName}'s recipes</button>
          <h2 style={styles.recipeTitle}>{selectedRecipe.title}</h2>
          <div style={styles.badgeRow}>
            {selectedRecipe.categories?.map((c) => (
              <span key={c} style={styles.badge}>{categoryLabel(c)}</span>
            ))}
          </div>
          <p style={styles.meta}>
            {selectedRecipe.prep_time ? `Prep: ${selectedRecipe.prep_time} min` : ''}
            {selectedRecipe.cook_time ? `  ·  Cook: ${selectedRecipe.cook_time} min` : ''}
            {selectedRecipe.servings ? `  ·  Serves: ${selectedRecipe.servings}` : ''}
          </p>

          {selectedRecipe.photo_url && (
            <img src={selectedRecipe.photo_url} alt={selectedRecipe.title} style={styles.photo} />
          )}

          <div style={styles.detailGrid}>
            <div>
              <h3 style={styles.sectionTitle}>Ingredients</h3>
              {selectedRecipe.ingredients?.length === 0 ? (
                <p style={styles.empty}>None listed</p>
              ) : (
                <ul style={styles.list}>
                  {selectedRecipe.ingredients?.map((ing) => (
                    <li key={ing.id}>{[ing.amount, ing.unit, ing.name].filter(Boolean).join(' ')}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 style={styles.sectionTitle}>Instructions</h3>
              {selectedRecipe.instructions?.length === 0 ? (
                <p style={styles.empty}>None listed</p>
              ) : (
                <ol style={styles.list}>
                  {selectedRecipe.instructions?.map((step) => (
                    <li key={step.id}>{step.instruction}</li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {selectedRecipe.notes && (
            <>
              <h3 style={styles.sectionTitle}>Notes</h3>
              <p style={styles.notes}>{selectedRecipe.notes}</p>
            </>
          )}
          {detailLoading && <p style={styles.status}>Loading...</p>}
        </div>
      ) : (
        <div style={styles.card}>
          <h2 style={styles.title}>👁️ {groupName ? `${groupName}'s Recipes` : 'Recipes'}</h2>
          <p style={styles.subtitle}>Read-only view for customer support — nothing here can be edited or deleted.</p>

          <input
            type="text"
            placeholder="Search recipes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />

          {error && <p style={styles.error}>{error}</p>}

          {recipes.length === 0 && !loading ? (
            <p style={styles.status}>No recipes found.</p>
          ) : (
            <div style={styles.grid}>
              {recipes.map((r) => (
                <div key={r.id} style={styles.recipeCard} onClick={() => openRecipe(r.id)}>
                  {r.photo_url ? (
                    <img src={r.photo_url} alt={r.title} style={styles.thumb} />
                  ) : (
                    <div style={styles.thumbPlaceholder}>🍽️</div>
                  )}
                  <p style={styles.recipeCardTitle}>{r.title}</p>
                  <div style={styles.badgeRowSmall}>
                    {r.categories?.slice(0, 2).map((c) => (
                      <span key={c} style={styles.badgeSmall}>{categoryLabel(c)}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '900px', margin: '0 auto' },
  backLink: { display: 'inline-block', color: '#e67e22', textDecoration: 'none', marginBottom: '16px', fontSize: '14px' },
  backBtn: { background: 'none', border: 'none', color: '#e67e22', cursor: 'pointer', fontSize: '13px', marginBottom: '16px', padding: 0 },
  card: {
    backgroundColor: 'white', padding: '32px', borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  title: { color: '#e67e22', marginBottom: '4px' },
  subtitle: { color: '#888', fontSize: '13px', marginBottom: '20px' },
  searchInput: {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', marginBottom: '20px', boxSizing: 'border-box',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' },
  recipeCard: { border: '1px solid #eee', borderRadius: '10px', padding: '10px', cursor: 'pointer' },
  thumb: { width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' },
  thumbPlaceholder: {
    width: '100%', height: '100px', borderRadius: '8px', backgroundColor: '#fdf0e3',
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px', fontSize: '24px',
  },
  recipeCardTitle: { fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '6px' },
  badgeRowSmall: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  badgeSmall: { backgroundColor: '#fdf0e3', color: '#e67e22', fontSize: '10px', padding: '2px 8px', borderRadius: '10px' },
  recipeTitle: { color: '#333', marginBottom: '10px' },
  badgeRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' },
  badge: { backgroundColor: '#fdf0e3', color: '#e67e22', fontSize: '12px', fontWeight: '600', padding: '4px 12px', borderRadius: '14px' },
  meta: { color: '#888', fontSize: '13px', marginBottom: '16px' },
  photo: { width: '100%', maxWidth: '320px', borderRadius: '10px', marginBottom: '20px' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '28px' },
  sectionTitle: { color: '#333', fontSize: '15px', marginBottom: '8px', marginTop: '4px' },
  list: { fontSize: '13px', color: '#555', lineHeight: 1.7, paddingLeft: '20px' },
  notes: { fontSize: '13px', color: '#666', whiteSpace: 'pre-wrap', marginTop: '4px' },
  empty: { color: '#bbb', fontSize: '13px' },
  status: { textAlign: 'center', color: '#999' },
  error: { color: '#e74c3c', textAlign: 'center', marginBottom: '16px' },
};

export default AdminGroupRecipes;
